import { ElementRef, HostBinding, Input, OnInit, Directive, HostListener, Inject, DOCUMENT } from '@angular/core';
import { ThemeType } from './shared/enums/theme-type.enum';
import { BehaviorSubject } from 'rxjs';

import { last } from 'rxjs/operators';

interface DwButtonInterface {
    themeType?: ThemeType;
    disabled?: boolean;
    tabIndex?: number;
    link?: string;
    href?: string;
    icon?: string;
    rel?: string;
    class?: string;
}

@Directive({
    selector: '[da-button]',
    standalone: false
})
export class DaButtonDirective implements OnInit {
    @Input() set themeType(value: ThemeType) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.themeType = value;
            this._state$.next(_state);
        }
    }
    @Input() set isDisabled(value: boolean) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.disabled = value;
            this._state$.next(_state);
        }
    }
    @Input() set tabIndex(value: number) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.tabIndex = value;
            this._state$.next(_state);
        }
    }
    @Input() set link(value: string) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.link = value;
            this._state$.next(_state);
        }
    }
    @Input() set href(value: string) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.href = value;
            this._state$.next(_state);
        }
    }
    @Input() set icon(value: string) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.icon = value;
            this._state$.next(_state);
        }
    }
    @Input() set rel(value: string) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.rel = value;
            this._state$.next(_state);
        }
    }
    @Input() set disabled(value: boolean) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.disabled = value;
            this._state$.next(_state);
        }
    }
    @Input() set class(value: string) {
        if (value != null) {
            const _state = this._state$.getValue();
            _state.class = value;
            this._state$.next(_state);
        }
    }

    private readonly _isButton: boolean;

    private _state$: BehaviorSubject<DwButtonInterface> = new BehaviorSubject({
        themeType: ThemeType.Secondary,
        disabled: false,
        tabIndex: 0,
        link: null,
        href: null,
        icon: null,
        rel: null
    });

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        if (this._disabled != null) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    @HostBinding('attr.tabindex') _tabIndex: number;
    @HostBinding('attr.href') _href: string;

    @HostBinding('attr.target') _target: string;
    @HostBinding('attr.disabled') _disabled: string;
    @HostBinding('attr.role') _role: string;
    @HostBinding('attr.rel') _rel: string;

    @HostBinding('class') _classes: string;
    private _classPrimary: boolean;
    private _classDisabled: boolean;
    private _classIcon: string;

    constructor(@Inject(DOCUMENT) private document: any, private _el: ElementRef) {
        this._isButton = _el.nativeElement.nodeName === 'BUTTON';
        this._role = this._isButton ? 'button' : null;
    }

    ngOnInit() {
        this._setData({
            themeType: ThemeType.Secondary,
            disabled: false,
            tabIndex: 0,
            link: null,
            href: null,
            icon: null,
            rel: null
        });
        this._state$.subscribe((data) => {
            this._setData(data);
        });
    }

    private _setData(data: any) {
        // Set disabled
        this._setDisabled(data.disabled);
        // Set tab index
        this._setTabIndex(data.tabIndex);
        // Set class
        this._setClass(data);
        // Set href
        this._setLink(data);
        // Set rel
        this._setRel(data);
    }

    private _setDisabled(value: boolean) {
        if (value != null) {
            this._classDisabled = value;
            this._disabled = value ? 'disabled' : null;
        }
    }

    private _setTabIndex(value: number) {
        if (value != null) {
            this._tabIndex = this._disabled != null ? -1 : value;
        }
    }

    private _setClass(data: any) {
        if (data.href != null || data.link != null || data.themeType) {
            const _link = data.link || data.href || null;
            const _themeType =
                _link != null && (_link.indexOf('.pdf') > -1 || _link.indexOf('.zip') > -1)
                    ? ThemeType.Primary
                    : data.themeType || ThemeType.Secondary;
            this._classPrimary = _themeType === ThemeType.Primary;
        }
        if (data.icon != null) {
            this._classIcon = data.icon;
        }
        this._classes = this._getClasses();
    }

    private _setLink(data: any) {
        if (data.href != null || data.link != null) {
            let _link = data.link || data.href;
            _link = this._getFormattedlink(_link);
            this._href = this._isButton || this.isDisabled ? null : _link;

            // Set link type
            const _linkType = this._getLinkType(_link.toLowerCase());
            this._target = _linkType === 'External' || _linkType === 'Download' ? '_blank' : '_self';
        }
    }

    private _setRel(data: any) {
        if (!this._isButton && (this._target === '_blank' || data.rel != null)) {
            this._rel = data.rel != null ? data.rel : 'noopener';
        }
    }

    private _getClasses(): string {
        const _classes = ['button'];

        if (this._classPrimary) {
            _classes.push('primary');
        } else {
            _classes.push('secondary');
        }

        if (this._classDisabled) {
            _classes.push('disabled');
        }

        if (this._classIcon != null) {
            _classes.push(this._classIcon);
        }

        if (this._state$.getValue().class) {
            _classes.push(this._state$.getValue().class);
        }
        return _classes.join(' ');
    }

    private _getLinkType(link): string {
        let linkType = 'Internal';
        // If the link starts with "http", it should be handled as external
        if (link) {
            if (link.indexOf('http') > -1) {
                linkType = 'External';
            }
            // Does the URL includes something like .draeger.com? If yes, it should be handled as internal
            if (link.indexOf('.draeger.com') > -1) {
                linkType = 'Internal';
                if (link.indexOf('static.draeger.com') > -1) {
                    linkType = 'External';
                }
            }

            if (link.indexOf('.pdf') > -1 || link.indexOf('.zip') > -1) {
                linkType = 'Download';
            }

            if (link.indexOf('#') === 0) {
                linkType = 'Anchor';
            }
        }
        return linkType;
    }

    private _getFormattedlink(link: string): string {
        let _formattedLink = link;
        const _isPhone = link && link.lastIndexOf('tel:', 0) === 0;
        const _isMailTo = link && link.lastIndexOf('mailto:', 0) === 0;
        const _isAnchor = link && link.indexOf('#') === 0;
        if (link && !_isPhone && !_isMailTo && !_isAnchor) {
            let linkType = this._getLinkType(link);
            if (linkType === 'Internal') {
                if (link[0] !== '#' && link[0] !== '/' && link.match(/^https?:\/\/.*/g) === null) {
                    _formattedLink = '/' + link;
                }
            }
        }
        if (link && _isAnchor) {
            _formattedLink = `${window.location.pathname}${link}`;
        }
        return _formattedLink;
    }
}
